"use client";

// The public Producer badge (free producer account, beta). One component for every place the
// label shows: the profile header, the talent card, the producer card and the post card.
//
// It is the member's own statement, not an ID check, so it is built to look nothing like the
// Verified badge: a squared amber tag with a clapperboard, where Verified is a round pink check
// (VerifiedBadge) or an emerald pill with a check (the profile header). It never sits inside the
// Verified badge, never shares its icon, color or wording, and reads only "Producer" or
// "Producer and talent".

import React from "react";
import { Clapperboard } from "lucide-react";
import { cn } from "@/lib/utils";
import { producerBadgeText, type ProducerRole } from "@/lib/producerLabel";

export interface ProducerBadgeProps {
  /** The api's role value; "producer_and_talent" reads "Producer and talent", anything else "Producer". */
  role?: ProducerRole | null;
  /** "dark" over a photo or banner, "light" on a card surface. */
  tone?: "dark" | "light";
  className?: string;
}

export default function ProducerBadge({ role = null, tone = "light", className }: ProducerBadgeProps) {
  const text = producerBadgeText(role);
  return (
    <span
      data-testid="producer-badge"
      data-role={role ?? "producer"}
      className={cn(
        "inline-flex max-w-full items-center gap-1 rounded-[4px] px-1.5 py-0.5 text-[11px] font-semibold leading-none whitespace-nowrap",
        tone === "dark"
          ? "bg-black/55 text-amber-200 ring-1 ring-inset ring-amber-300/50 backdrop-blur-sm"
          : "bg-amber-50 text-amber-900 ring-1 ring-inset ring-amber-300 dark:bg-amber-950/40 dark:text-amber-200 dark:ring-amber-700/60",
        className
      )}
    >
      <Clapperboard className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span>{text}</span>
    </span>
  );
}
